import type { MessageDescriptor } from 'react-intl'
import { defineMessages } from 'react-intl'

interface BaseLoyaltyCampaignOffer {
  title: MessageDescriptor
  description: MessageDescriptor
  link: string
  imageId: string
  /** If specified, offer only shows in these regions. If omitted, shows in all regions. */
  regions?: Array<string>
}

/**
 * Campaign offer controlled by custom rule (shows only when rule is active)
 * Can specify multiple rule IDs (e.g., one for staging, one for prod)
 */
interface RuleBasedCampaignOffer extends BaseLoyaltyCampaignOffer {
  customRuleIds: Array<string>
}

/**
 * Campaign offer that is always enabled (not tied to custom rules)
 */
interface AlwaysEnabledCampaignOffer extends BaseLoyaltyCampaignOffer {
  forceEnable: true
}

export type LoyaltyCampaignOffer = RuleBasedCampaignOffer | AlwaysEnabledCampaignOffer

const messages = defineMessages({
  doubleStatusCreditsHotelTitle: {
    id: 'hsOUW2XaPpjZmC0SVh6a',
    defaultMessage: 'Double Status Credits on hotel stays',
    description: 'Campaign offer title for double status credits on hotel bookings',
  },
  doubleStatusCreditsHotelDescription: {
    id: 'Ghwp6YQ_MbW1XG2rM-lV',
    defaultMessage: 'Stay somewhere special and get rewarded twice. Book one of our curated hotel escapes and earn Double Status Credits. Ending 24 December 2025.',
    description: 'Campaign offer description for double status credits on hotel bookings',
  },
  doubleStatusCreditsToursTitle: {
    id: 'aDI-MY4vaOwQjfF0_BP0',
    defaultMessage: 'Double Status Credits on handpicked tours',
    description: 'Campaign offer title for double status credits on tour bookings',
  },
  doubleStatusCreditsToursDescription: {
    id: 'gkZo7MZ-g4sjFduCm03x',
    defaultMessage: 'Go beyond the ordinary with immersive, expertly curated tours. From ancient wonders to cultural deep-dives, enjoy Double Status Credits as you discover the world. Ending 24 December 2025.',
    description: 'Campaign offer description for double status credits on tour bookings',
  },
  doubleStatusCreditsCruisesTitle: {
    id: 'D1VnDfMGiMFsMqDiJNqx',
    defaultMessage: 'Double Status Credits on cruise bookings',
    description: 'Campaign offer title for double status credits on cruise bookings',
  },
  doubleStatusCreditsCruisesDescription: {
    id: 'TkEV1g2YebzI7H_ZG02a',
    defaultMessage: 'Set sail in style and unlock your next status tier faster. Book from our exclusive cruise collection and earn Double Status Credits on every journey. Ending 24 December 2025.',
    description: 'Campaign offer description for double status credits on cruise bookings',
  },
  triplePointsShopTitle: {
    id: 'nD4Ddr2Ih9My31rD7Pzp',
    defaultMessage: 'Triple Société Points on every Shop purchase',
    description: 'Campaign offer title for triple points on shop purchases',
  },
  triplePointsShopDescription: {
    id: 'j-PvnzJAjwZJUMfl8lIX',
    defaultMessage: 'Enjoy 3× points on all products from the Luxury Escapes Shop this month. Ending 24 December 2025.',
    description: 'Campaign offer description for triple points on shop purchases',
  },
  triplePointsGarudaTitle: {
    id: 'lif8je0y5XTTixfif4bj',
    defaultMessage: 'Triple Points on Garuda Indonesia',
    description: 'Campaign offer title for triple points on Garuda Indonesia flights',
  },
  triplePointsGarudaDescription: {
    id: '2rl4zCSU-TW025DSMuLi',
    defaultMessage: 'Skip the queue with 24/7 access to our dedicated support team, always here to help, whenever you need us.',
    description: 'Campaign offer description for triple points on Garuda Airways flights',
  },
  doublePointsUltraLuxTitle: {
    id: '3E6rLHIGnoYLv6jHurhY',
    defaultMessage: 'Double Points on Ultra Lux escapes',
    description: 'Campaign offer title for double points on Ultra Lux bookings',
  },
  doublePointsUltraLuxDescription: {
    id: 'lX-ByK4Oo2schlzQs2ig',
    defaultMessage: 'When only the best will do. Book from our Ultra Lux collection and earn Double Points.',
    description: 'Campaign offer description for double points on Ultra Lux bookings',
  },
})

export const LOYALTY_CAMPAIGN_OFFERS: Array<LoyaltyCampaignOffer> = [
  {
    title: messages.doubleStatusCreditsHotelTitle,
    description: messages.doubleStatusCreditsHotelDescription,
    link: '/hotels',
    imageId: 'ejtmomf9dsndnxz99v',
    customRuleIds: [
      '4e5b3afe-ad26-4324-9972-6c4c982dca95',
      'c170c9d8-e79d-4005-aa56-6f8bc1aafd3b',
    ],
  },
  {
    title: messages.doubleStatusCreditsToursTitle,
    description: messages.doubleStatusCreditsToursDescription,
    link: '/tours',
    imageId: 'bv02lcvv3svuoiezez',
    customRuleIds: [
      '4e5b3afe-ad26-4324-9972-6c4c982dca95',
      'c170c9d8-e79d-4005-aa56-6f8bc1aafd3b',
    ],
  },
  {
    title: messages.doubleStatusCreditsCruisesTitle,
    description: messages.doubleStatusCreditsCruisesDescription,
    link: '/cruises',
    imageId: 'fnkxq89apjdnklxifg',
    customRuleIds: [
      '4e5b3afe-ad26-4324-9972-6c4c982dca95',
      'c170c9d8-e79d-4005-aa56-6f8bc1aafd3b',
    ],
  },
  {
    title: messages.triplePointsShopTitle,
    description: messages.triplePointsShopDescription,
    link: '/shop',
    imageId: 'rud239l2q5j81e8v5uob',
    regions: ['AU'],
    customRuleIds: [
      '1474f4db-ed16-47e9-8696-03f9f31cf606',
    ],
  },
  {
    title: messages.triplePointsGarudaTitle,
    description: messages.triplePointsGarudaDescription,
    link: '/flights',
    imageId: 'rj8rmpfex9caygh3g1',
    customRuleIds: [
    ],
  },
  {
    title: messages.doublePointsUltraLuxTitle,
    description: messages.doublePointsUltraLuxDescription,
    link: '/ultra-lux',
    imageId: 'gkc3cki0fnhpxzswubv',
    customRuleIds: [
      '628c9023-0529-4547-beba-f101928245cf',
      // prod rule id
      'bdfe3e73-b208-4d20-9d36-ef1a64c65945',
    ],
  },
]
